import { consoleRoutes, CONVERSATION_PRIORITIES, WORKFLOW_STEP_MIN_PLAN, type ConversationPriority } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Field, Icon, Select, Skeleton } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { isPlanGateError, useSlaPlan, useSlaPolicies } from "../../../workspace/shared";
import type { ApplySlaStep, SetPriorityStep } from "../types";
import { type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";
const SLA_PLAN = WORKFLOW_STEP_MIN_PLAN.apply_sla ?? "institution";

/** Shown instead of the policy list when the plan has no SLA policies: what they do and where to upgrade. */
function SlaUpgrade() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <div className="space-y-3 rounded-md border border-line bg-surface-2 p-4" data-testid="sla-upgrade">
      <div className="flex items-center gap-2">
        <Icon name="lock" className="size-4 text-fg-subtle" />
        <span className="text-sm font-bold">{t(`${F}.slaGateTitle`)}</span>
        <Badge tone="warning">{t(`console.plans.${SLA_PLAN}`)}</Badge>
      </div>
      <p className="text-xs text-fg-muted">{t(`${F}.slaGateBody`)}</p>
      <AppLink className="text-xs font-semibold text-primary-ink underline" href={consoleRoutes.billing(summary.slug)}>{t(`${F}.slaUpgrade`)}</AppLink>
    </div>
  );
}

/** Apply SLA: pick a policy; its clocks start on the conversation and raise the SLA triggers. */
export function ApplySlaForm({ step, disabled, onChange }: FormProps<ApplySlaStep>) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { ready, blocked } = useSlaPlan();
  const policies = useSlaPolicies(ready && !blocked);
  if (blocked || isPlanGateError(policies.error)) return <SlaUpgrade />;
  if (!ready || policies.isPending) return <Skeleton className="h-16 w-full" aria-hidden="true" />;
  const list = policies.data ?? [];
  return (
    <div className="space-y-3">
      <Field label={t(`${F}.slaPolicy`)} hint={t(`${F}.slaPolicyHint`)}>
        <Select value={step.policy_id} disabled={disabled || list.length === 0} onChange={(e) => { onChange({ ...step, policy_id: e.target.value }); }}>
          <option value="">{t(`${F}.choose`)}</option>
          {list.map((policy) => <option key={policy.id} value={policy.id}>{policy.name}</option>)}
        </Select>
      </Field>
      {policies.isError || list.length === 0 ? (
        <p className="text-xs text-fg-muted" data-testid="sla-empty">
          {t(policies.isError ? `${F}.slaLoadFailed` : `${F}.slaNone`)}{" "}
          <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.settings(summary.slug, "sla")}>{t(`${F}.slaOpenSettings`)}</AppLink>
        </p>
      ) : null}
    </div>
  );
}

/** Set priority: normal, high or urgent (Inbox sorts and filters on it). */
export function SetPriorityForm({ step, disabled, onChange }: FormProps<SetPriorityStep>) {
  const { t } = useTranslation();
  return (
    <Field label={t(`${F}.priority`)} hint={t(`${F}.priorityHint`)}>
      <Select value={step.priority} disabled={disabled} onChange={(e) => { onChange({ ...step, priority: e.target.value as ConversationPriority }); }}>
        {CONVERSATION_PRIORITIES.map((priority) => <option key={priority} value={priority}>{t(`engage.inbox.priority.${priority}`)}</option>)}
      </Select>
    </Field>
  );
}
